import { useState } from 'react';
import { unionRoleLabel } from '../../../shared/domain/unionAccess.js';
import { useBlablaLinkProfileAvatar } from '../../game-profile/hooks/useBlablaLinkProfileAvatar.js';
import { BlablaLinkProfileAvatar } from '../../game-profile/shared/BlablaLinkProfileAvatar.jsx';
import {
  UnionMemberManagementTabs,
  UnionRosterPanel,
} from '../../union-roster/shared/UnionRosterPanel.jsx';
import { leadershipTransferConfirmation } from '../domain/unionLeadershipTransfer.js';
import { useUnionMemberManagement } from '../hooks/useUnionMemberManagement.js';

function formatJoinedAt(value) {
  if (!value) return '확인 불가';

  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(value));
  } catch {
    return '확인 불가';
  }
}

function MobileUnionAccountDirectory() {
  const management = useUnionMemberManagement();
  const profileAvatar = useBlablaLinkProfileAvatar();

  return (
    <section className="mobile-union-member-management" aria-label="사이트 가입 계정 관리">
      <div className="mobile-union-member-management-heading">
        <div>
          <p className="section-eyebrow">SITE ACCOUNTS</p>
          <h2>{management.unionName || '유니온'} 가입 계정</h2>
          <p>사이트에 가입한 계정의 역할을 관리합니다. 게임 멤버 명단과는 별도입니다.</p>
        </div>
        <strong>{management.members.length}</strong>
      </div>

      {management.loading ? (
        <div className="mobile-union-member-management-loading" role="status">
          <span aria-hidden="true" />
          <p>가입 계정 목록 확인 중</p>
        </div>
      ) : management.members.length === 0 ? (
        <div className="mobile-union-member-management-empty">
          <strong>활성 가입 계정 없음</strong>
          <p>가입 상태 또는 Supabase 실행 대기 SQL을 확인해주세요.</p>
        </div>
      ) : (
        <div className="mobile-union-member-management-list">
          {management.members.map((member) => {
            const isProcessing = management.actionMembershipId === member.membershipId;
            const nextRole = member.role === 'deputy' ? 'member' : 'deputy';
            const promotionBlocked = nextRole === 'deputy' && !member.guildMatches;
            const isTransferring = isProcessing && management.actionMode === 'leadership';
            const avatarUrl = member.avatarUrl || (member.isSelf ? profileAvatar?.avatarUrl : '');

            return (
              <article className={`mobile-union-member-management-card role-${member.role}`} key={member.membershipId}>
                <div className="mobile-union-member-management-card-head">
                  <BlablaLinkProfileAvatar
                    className="mobile-union-member-management-avatar"
                    nickname={member.nickname}
                    avatarUrl={avatarUrl}
                  />
                  <div>
                    <small>{member.isSelf ? 'MY PROFILE' : 'SITE ACCOUNT'}</small>
                    <h3>{member.nickname || '닉네임 확인 불가'}</h3>
                  </div>
                  <span>{unionRoleLabel(member.role)}</span>
                </div>

                <dl>
                  <div>
                    <dt>가입일</dt>
                    <dd>{formatJoinedAt(member.joinedAt)}</dd>
                  </div>
                  <div>
                    <dt>게임 소속</dt>
                    <dd className={member.guildMatches ? 'matched' : 'mismatch'}>
                      {member.guildMatches ? '일치' : '불일치'}
                    </dd>
                  </div>
                </dl>

                <div className="mobile-union-member-management-actions">
                  {member.canTransferLeadership ? (
                    <button
                      className="transfer-leader"
                      type="button"
                      disabled={Boolean(management.actionMembershipId)}
                      onClick={() => {
                        const confirmed = window.confirm(leadershipTransferConfirmation(member.nickname));
                        if (confirmed) {
                          management.transferLeadership({
                            membershipId: member.membershipId,
                            nickname: member.nickname,
                          });
                        }
                      }}
                    >
                      {isTransferring ? '이전 중…' : '주딱 넘기기'}
                    </button>
                  ) : null}

                  {member.canManageRoles ? (
                    <button
                      className="role-change"
                      type="button"
                      disabled={Boolean(management.actionMembershipId) || promotionBlocked}
                      onClick={() => management.changeRole({
                        membershipId: member.membershipId,
                        nickname: member.nickname,
                        role: nextRole,
                      })}
                    >
                      {isProcessing && management.actionMode === 'role'
                        ? '처리 중…'
                        : nextRole === 'deputy'
                          ? '파딱 지정'
                          : '멤버 전환'}
                    </button>
                  ) : null}

                  {member.canRemove ? (
                    <button
                      className="remove"
                      type="button"
                      disabled={Boolean(management.actionMembershipId)}
                      onClick={() => {
                        const confirmed = window.confirm(`${member.nickname || '이 멤버'} 계정을 유니온에서 제외할까요?`);
                        if (confirmed) {
                          management.remove({
                            membershipId: member.membershipId,
                            nickname: member.nickname,
                          });
                        }
                      }}
                    >
                      {isProcessing && management.actionMode === 'remove' ? '처리 중…' : '제외'}
                    </button>
                  ) : null}

                  {!member.canTransferLeadership && !member.canManageRoles && !member.canRemove ? (
                    <span className="readonly">조회 전용</span>
                  ) : null}
                </div>

                {promotionBlocked && member.canManageRoles ? (
                  <p className="mobile-union-member-management-warning">소속 불일치 상태에서는 파딱 지정과 주딱 이전이 차단됩니다.</p>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {management.notice.message ? (
        <p className={`mobile-union-member-management-notice ${management.notice.type}`} role="status">
          {management.notice.message}
        </p>
      ) : null}
    </section>
  );
}

export function MobileUnionMemberManagement() {
  const [view, setView] = useState('roster');

  return (
    <div className="union-member-management-page mobile-union-member-management-page">
      <UnionMemberManagementTabs value={view} onChange={setView} />
      <div role="tabpanel">
        {view === 'roster' ? <UnionRosterPanel mobile /> : <MobileUnionAccountDirectory />}
      </div>
    </div>
  );
}
